import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import {
  GlobalAttributesDocument,
  useUpdateGlobalAttributeMutation,
} from "#graphql/supergraphHooks";
import {
  type UpdateAttributeInput,
  type UpdateGlobalAttributeMutation,
  type UpdateGlobalAttributeMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseUpdateGlobalAttributeReturn {
  updateGlobalAttribute: (
    id: string,
    input: UpdateAttributeInput,
  ) => Promise<UpdateGlobalAttributeMutation["updateGlobalAttribute"] | null>;
  loading: boolean;
  error: ReturnType<typeof useUpdateGlobalAttributeMutation>[1]["error"];
}

/**
 * DV-204 — edit a global attribute. `name` / `description` and a select type's
 * value set (`options`, full-state) are mutable; `valueType` / `referenceTarget`
 * are immutable post-creation. Refetches the `GlobalAttributes` catalog (which
 * feeds `globalLib`) so the edit is reflected in mounted lists — reads are
 * `no-cache`, so nothing else updates.
 */
export const useUpdateGlobalAttribute = (
  options?: MutationHookOptions<
    UpdateGlobalAttributeMutation,
    UpdateGlobalAttributeMutationVariables
  >,
): UseUpdateGlobalAttributeReturn => {
  const [mutate, { loading, error }] = useUpdateGlobalAttributeMutation({
    refetchQueries: [GlobalAttributesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const updateGlobalAttribute = useCallback(
    async (id: string, input: UpdateAttributeInput) => {
      const { data } = await mutate({ variables: { id, input } });
      return data?.updateGlobalAttribute ?? null;
    },
    [mutate],
  );

  return { updateGlobalAttribute, loading, error };
};
